import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect, batch} from 'react-redux';
import {FmlxLabel, FmlxRadioButton, FmlxTextBox} from 'fmlx-common-ui';
import {padWithZeros} from 'lib/numericHelper';
import FastModal from '../FastModal';
import FormattedHTMLMessage from '../FormattedHTMLMessage';
import {SignalREvent, ViewMode, SetTrayModalType, FastRobotErrorcode, TrayType} from '../../constants/dataEnum';
import {setTrayId} from '../../stores/business/instrumentBusiness';
import {safeScanTrayId} from '../../stores/business/trayBusiness';
import {
  hideModalSetTrayID,
  resetModalSetTrayID,
  showModalSetTrayID,
} from '../../stores/actions/modalSetTrayIDStateAction';
import {setTrayID, setTrayData, setTrayType} from '../../stores/actions/trayCalibrationStateAction';
import {changeView, showLoading, hideLoading} from '../../stores/actions/appStateAction';
import {setWizardFlag} from '../../stores/actions/wizardStateAction';
import {clearModalError} from '../../stores/actions/modalStateAction';
import {showModalConnectionFailed} from '../../stores/business/commonModalBusiness';
import {fastCommonActuatorAPIErrorHandler} from '../../stores/business/errorHandlerBusiness';
import {newProtocol} from '../../stores/business/protocolManagementBusiness';
import withIntl from '../../lib/withIntl';

import './ModalSetTrayID.scss';

const DIGIT_TRAY_ID_COUNT = 5;
function ModalSetTrayID(props) {
  const {
    onChangeView,
    onShowLoading,
    onHideLoading,
    modalSetTrayIDState,
    onShowModalConnectionFailed,
    onHideModalSetTrayID,
    onShowModalSetTrayID,
    onResetModalSetTrayID,
    onSetTrayID,
    onSetTrayType,
    onSetTrayData,
    instrumentBusinessSetTrayId,
    onSafeScanTrayId,
    onFastCommonActuatorAPIErrorHandler,
    modalStateErrorCodeNumber,
    modalStateActionClearModalError,
    protocolManagementBusinessNewProtocol,
    viewMode,
    intl,
  } = props;

  const {show, type} = modalSetTrayIDState;

  const [trayInputValue, setTrayInputValue] = useState(padWithZeros(1, DIGIT_TRAY_ID_COUNT));
  const [trayTypeValue, setTrayTypeValue] = useState(TrayType.NORMAL_CADDY);
  const [manualMode, setManualMode] = useState(false);
  const [isValid, setIsValid] = useState(true);

  function modalTrayIDContentCompenent(inputValue) {
    return (
      <div className="modal-set-tray-id__content">
        <span>{intl.formatMessage({id: 'modal.setTrayId.content'})}</span>
        <div className="modal-set-tray-id__content__input">
          <FmlxLabel label={intl.formatMessage({id: 'modal.setTrayId.label'})} />
          <FmlxTextBox
            value={inputValue}
            type="number"
            onChange={(e) => onChangeHandler(e.value)}
            onKeyDown={
              (evt) =>
                // eslint-disable-next-line implicit-arrow-linebreak
                (evt.key.toLocaleLowerCase === 'e' || evt.key === '-' || evt.key === '+' || evt.key === '.') &&
                evt.preventDefault()
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </div>
        <div className="modal-set-tray-id__content__input">
          <FmlxLabel label="Tray Type" />
          <FmlxRadioButton
            selectedIndex={Object.values(TrayType).indexOf(trayTypeValue)}
            size="sm"
            items={[{content: 'Single Caddy'}, {content: 'Dual Caddy'}, {content: 'High Volume Caddy'}]}
            direction="row"
            onChange={({index}) => setTrayTypeValue(Object.values(TrayType)[index])}
          />
        </div>
      </div>
    );
  }

  const [modalTrayIDContent, setModalTrayIDContent] = useState(modalTrayIDContentCompenent(trayInputValue));

  function onChangeHandler(newValue) {
    const newValueConverted = parseInt(newValue, 10);

    if (newValueConverted <= 99999) {
      const convertedValue = padWithZeros(newValueConverted, DIGIT_TRAY_ID_COUNT);
      setIsValid(convertedValue !== padWithZeros(0, DIGIT_TRAY_ID_COUNT));
      setTrayInputValue(convertedValue);
      setModalTrayIDContent(modalTrayIDContentCompenent(convertedValue));
    }
  }

  const doOnHideModalSetTrayID = () => {
    onHideModalSetTrayID();
    modalStateActionClearModalError();

    setTimeout(() => {
      setManualMode(false);
    }, 100);
  };

  const buttonManualMode = [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: () => {
        instrumentBusinessSetTrayId(Number(trayInputValue), trayTypeValue)
          .then((res) => {
            batch(() => {
              onSetTrayID(res.tray.id);
              onSetTrayType(res.tray.trayType);
              onSetTrayData(res.tray);
              setTrayTypeValue(TrayType.NORMAL_CADDY);
              doOnHideModalSetTrayID();

              if (type === SetTrayModalType.TRAY_CALIBRATION) {
                onChangeView(ViewMode.TRAY_CALIBRATION);
              }
              if (type === SetTrayModalType.PROTOCOL_EDITOR && viewMode !== ViewMode.PROTOCOL_EDITOR) {
                protocolManagementBusinessNewProtocol();
              }
            });
          })
          .catch((err) => {
            console.log(SignalREvent.SET_TRAY_ID, 'ERROR ', err);
            doOnHideModalSetTrayID();
            onShowModalConnectionFailed();
          });
      },
      text: intl.formatMessage({id: 'modal.setTrayId.firstButton'}),
    },
  ];

  const button = [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: async () => {
        onHideModalSetTrayID();
        onShowLoading();

        const response = await onSafeScanTrayId();

        onHideLoading();

        if (response !== 'noError') {
          let callback;
          if (response === FastRobotErrorcode.TouchProbeExpanded) {
            callback = () => onShowModalSetTrayID(type);
          }
          onFastCommonActuatorAPIErrorHandler(response, callback);
          return;
        }

        doOnHideModalSetTrayID();
        onResetModalSetTrayID();

        if (type === SetTrayModalType.TRAY_CALIBRATION) {
          onChangeView(ViewMode.TRAY_CALIBRATION);
        }
        if (type === SetTrayModalType.PROTOCOL_EDITOR && viewMode !== ViewMode.PROTOCOL_EDITOR) {
          const {isTessara} = modalSetTrayIDState?.param ?? {};
          protocolManagementBusinessNewProtocol(false, isTessara);
        }
      },
      text: intl.formatMessage({id: 'modal.setTrayIdRescan.firstButton'}),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: () => {
        setManualMode(true);
      },
      text: intl.formatMessage({id: 'modal.setTrayIdRescan.secondButton'}),
    },
  ];

  return (
    <FastModal
      isValid={isValid}
      className={manualMode ? 'setTrayIdModal' : 'rescanTrayIdModal'}
      show={show}
      btnAction={manualMode ? buttonManualMode : button}
      header={intl.formatMessage({id: manualMode ? 'modal.setTrayId.title' : 'modal.setTrayIdRescan.title'})}
      content={manualMode ? modalTrayIDContent : <FormattedHTMLMessage id="modal.setTrayIdRescan.content" />}
      onClose={doOnHideModalSetTrayID}
      errorCodeNumber={manualMode ? 0 : modalStateErrorCodeNumber}
      size={manualMode ? 'lg' : 'md'}
      closeDisabled={viewMode === ViewMode.SETUP_WIZARD}
    />
  );
}

function mapStateToProps(state) {
  return {
    modalSetTrayIDState: state.modalSetTrayIDState,
    currentWizardView: state.wizardState.currentWizardView,
    modalStateErrorCodeNumber: state.modalState.errorCodeNumber,
    viewMode: state.appState.viewMode,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onChangeView: bindActionCreators(changeView, dispatch),
    onShowLoading: bindActionCreators(showLoading, dispatch),
    onHideLoading: bindActionCreators(hideLoading, dispatch),
    onSetTrayID: bindActionCreators(setTrayID, dispatch),
    onSetTrayType: bindActionCreators(setTrayType, dispatch),
    onSetTrayData: bindActionCreators(setTrayData, dispatch),
    onShowModalConnectionFailed: bindActionCreators(showModalConnectionFailed, dispatch),
    onHideModalSetTrayID: bindActionCreators(hideModalSetTrayID, dispatch),
    onResetModalSetTrayID: bindActionCreators(resetModalSetTrayID, dispatch),
    onShowModalSetTrayID: bindActionCreators(showModalSetTrayID, dispatch),
    instrumentBusinessSetTrayId: bindActionCreators(setTrayId, dispatch),
    onSafeScanTrayId: bindActionCreators(safeScanTrayId, dispatch),
    onsetWizardFlag: bindActionCreators(setWizardFlag, dispatch),
    onFastCommonActuatorAPIErrorHandler: bindActionCreators(fastCommonActuatorAPIErrorHandler, dispatch),
    modalStateActionClearModalError: bindActionCreators(clearModalError, dispatch),
    protocolManagementBusinessNewProtocol: bindActionCreators(newProtocol, dispatch),
  };
}

ModalSetTrayID.propTypes = {
  onChangeView: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  modalSetTrayIDState: PropTypes.object.isRequired,
  onShowModalConnectionFailed: PropTypes.func.isRequired,
  onHideModalSetTrayID: PropTypes.func.isRequired,
  onShowModalSetTrayID: PropTypes.func.isRequired,
  onResetModalSetTrayID: PropTypes.func.isRequired,
  onSetTrayID: PropTypes.func.isRequired,
  onSetTrayType: PropTypes.func.isRequired,
  onSetTrayData: PropTypes.func.isRequired,
  instrumentBusinessSetTrayId: PropTypes.func.isRequired,
  onSafeScanTrayId: PropTypes.func.isRequired,
  onFastCommonActuatorAPIErrorHandler: PropTypes.func.isRequired,
  modalStateErrorCodeNumber: PropTypes.number.isRequired,
  modalStateActionClearModalError: PropTypes.func.isRequired,
  protocolManagementBusinessNewProtocol: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  viewMode: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(ModalSetTrayID));
